// src/pages/authn/AuthnAdminPage.tsx
// Authn admin console — SSO/OIDC provider registry, discovery reference, and
// token diagnostics. Admin-gated (AdminRoute, in App.tsx). See docs/ux/authn-ia.md.

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { SsoAdminPanel } from '@/features/authn/SsoAdminPanel';
import { OidcDiscoveryPanel } from '@/features/authn/OidcDiscoveryPanel';
import { TokenToolsPanel } from '@/features/authn/TokenToolsPanel';

type Tab = 'providers' | 'discovery' | 'tokens';
const TABS: Tab[] = ['providers', 'discovery', 'tokens'];

export const AuthnAdminPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'providers';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{t('authn.admin.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('authn.admin.subtitle')}</p>
      </div>

      <div role="tablist" aria-label={t('authn.admin.title')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`authn.admin.tab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'providers' && <SsoAdminPanel />}
      {tab === 'discovery' && <OidcDiscoveryPanel />}
      {tab === 'tokens' && <TokenToolsPanel />}
    </div>
  );
});

export default AuthnAdminPage;
